/**
 * Daily Reference Intake (DRI) values for nutrients
 * Based on average adult requirements
 * Sources: USDA, WHO, and FDA recommendations
 */

export const NUTRIENT_REFERENCE_VALUES: Record<string, number> = {
  // Macronutrients
  protein: 50,        // g
  fat: 70,            // g
  energy: 2000,       // kcal
  
  // Omega fatty acids
  omega3: 1600,       // mg (1.6g)
  omega6: 17000,      // mg (17g)
  
  // Major minerals
  calcium: 1000,      // mg
  iron: 18,           // mg
  zinc: 11,           // mg
  magnesium: 400,     // mg
  phosphorus: 700,    // mg
  potassium: 3500,    // mg
  sodium: 2300,       // mg (upper limit)
  
  // Trace minerals
  iodine: 150,        // μg
  copper: 0.9,        // mg
  manganese: 2.3,     // mg
  selenium: 55,       // μg
  sulphur: 500,       // mg (estimated)
  
  // Fat-soluble vitamins
  vitaminA: 900,      // μg RAE
  vitaminD: 20,       // μg
  vitaminE: 15,       // mg
  
  // Water-soluble vitamins (B-complex)
  thiamin: 1.2,       // mg (B1)
  riboflavin: 1.3,    // mg (B2)
  niacin: 16,         // mg (B3)
  vitaminB6: 1.7,     // mg
  folate: 400,        // μg (B9)
  vitaminB12: 2.4,    // μg
  
  // Other
  carbohydrates: 300, // g
};

/**
 * Get the reference value for a nutrient
 * @param nutrientKey - The nutrient identifier
 * @returns The daily reference value, or null if not found
 */
export function getNutrientReferenceValue(nutrientKey: string): number | null {
  return NUTRIENT_REFERENCE_VALUES[nutrientKey] ?? null;
}

/**
 * Calculate percentage of daily reference intake
 * @param nutrientKey - The nutrient identifier
 * @param value - The actual nutrient value
 * @returns Percentage of DRI, or null if reference value not found
 */
export function calculatePercentDRI(nutrientKey: string, value: number): number | null {
  const reference = getNutrientReferenceValue(nutrientKey);
  if (reference === null) return null;
  return (value / reference) * 100;
}
